import { isLoopbackUrl } from "@amtana/module";
import { z } from "zod";

// Slack as a delivery channel, staff-only (ADR-022 point 6, S26.07, S26.08). Staff add each
// channel in the back office (/ops, through ops's peer-only doors into NotificationApi below): a
// name, an incoming-webhook URL and a closed set of facts it hears. The webhook URL is sealed at
// rest (ADR-008 point 3) and never answered back, not even its last characters: a channel's view
// never carries it. The facts themselves do not exist yet (S26.02-S26.04); this topic catalogue
// is what S26.08 will map facts onto.
//
// Security finding, PR #105 (D524): an open `z.url()` let staff — or a forged `/ops` request —
// point a channel at an attacker's server, which then receives every fact that channel hears,
// including a delivery's own recipient address. The schema (pure, no environment of its own,
// `rules-are-pure`'s shape) now only shapes an `https://hooks.slack.com/...` webhook, with a
// loopback URL (slacksim) allowed structurally too, since a contract cannot tell development from
// production. `slackWebhookUrlProductionProblem` draws the line the service (`SlackChannelService`,
// the only owner of `ModuleSetup.production`) checks at the write, mirroring `paymentApiBaseProblem`
// (`modules/billing/contract/src/payment-providers.schemas.ts`), the same "shape here, production
// refusal at the write" split `Config.define`'s own `refuseInProduction` takes for an env-sourced
// value.

/** A Slack incoming webhook's real host; every production webhook URL is `https://` + this. */
export const SLACK_WEBHOOK_HOST = "hooks.slack.com";

/** An `https://` URL's own host, read without the `URL` global so this package stays
 * browser-safe (the same reasoning `@amtana/module`'s own `isLoopbackUrl` gives); stops at the
 * first `/`, `:`, `?` or `#`, so a redirector naming the real host only in its path or query
 * (`https://evil.test/redirect?to=https://hooks.slack.com/x`) never matches. */
const HTTPS_HOST = /^https:\/\/(?:[^/@]*@)?([^/:?#]+)/i;

function isRealSlackWebhookUrl(value: string): boolean {
  return HTTPS_HOST.exec(value)?.[1] === SLACK_WEBHOOK_HOST;
}

export const SLACK_TOPICS = ["sign-ups", "payments", "delivery-failures"] as const;
export const slackTopicSchema = z.enum(SLACK_TOPICS);
export type SlackTopic = (typeof SLACK_TOPICS)[number];

export const SLACK_CHANNEL_NAME_MAX = 80;
/** A channel's own name, as staff type it: typically `#signups`, but free text, so a name like
 * "Payments (EU)" works too. */
export const slackChannelNameSchema = z.string().trim().min(1).max(SLACK_CHANNEL_NAME_MAX);

/** A Slack incoming webhook: `https://hooks.slack.com/...` in production; a loopback simulator in
 * development and tests (ADR-022 point 6, like every other simulated channel's URL). Anything
 * else — another host, another protocol, `javascript:`, a redirector — is refused outright: a
 * webhook URL is where every fact that channel hears gets sent, so it is never free text. */
export const slackWebhookUrlSchema = z
  .url()
  .refine((value) => isRealSlackWebhookUrl(value) || isLoopbackUrl(value), {
    message: `must be an https://${SLACK_WEBHOOK_HOST}/... incoming webhook URL (a loopback URL is allowed only in development, for slacksim)`,
  });

/** Why production refuses storing this webhook URL, or undefined when it may (ADR-022 point 6,
 * D524): a loopback host is slacksim, fine in development, never once `NODE_ENV` is
 * "production" — the same shape `paymentApiBaseProblem` already takes for billing's webhook. */
export function slackWebhookUrlProductionProblem(value: string): string | undefined {
  return isLoopbackUrl(value) ? "points at a loopback host, slacksim" : undefined;
}

/** A channel as staff see it: never the webhook URL (ADR-022 point 6, the "never shown back"
 * scenario). */
export const slackChannelSchema = z.object({
  id: z.string(),
  name: slackChannelNameSchema,
  topics: z.array(slackTopicSchema),
  setByUserId: z.string(),
  setAt: z.iso.datetime(),
});
export type SlackChannel = z.infer<typeof slackChannelSchema>;

export const addSlackChannelInputSchema = z.object({
  name: slackChannelNameSchema,
  webhookUrl: slackWebhookUrlSchema,
  topics: z.array(slackTopicSchema).min(1, "Choose at least one fact for this channel to hear."),
});
export type AddSlackChannelInput = z.infer<typeof addSlackChannelInputSchema>;

/** Each field left out keeps its stored value; `webhookUrl` given replaces it (never shown back
 * to be edited in place). */
export const updateSlackChannelInputSchema = z.object({
  id: z.string(),
  name: slackChannelNameSchema.optional(),
  webhookUrl: slackWebhookUrlSchema.optional(),
  topics: z.array(slackTopicSchema).min(1, "Choose at least one fact for this channel to hear.").optional(),
});
export type UpdateSlackChannelInput = z.infer<typeof updateSlackChannelInputSchema>;

export const removeSlackChannelInputSchema = z.object({ id: z.string() });
export type RemoveSlackChannelInput = z.infer<typeof removeSlackChannelInputSchema>;

export const testPostSlackChannelInputSchema = z.object({ id: z.string() });
export type TestPostSlackChannelInput = z.infer<typeof testPostSlackChannelInputSchema>;

export const testPostSlackChannelOutputSchema = z.object({ ok: z.literal(true) });
export type TestPostSlackChannelOutput = z.infer<typeof testPostSlackChannelOutputSchema>;

export const listSlackChannelsOutputSchema = z.array(slackChannelSchema);
